import { AfterViewInit, Component, effect, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { GlobalsService, AuthService, LoginService, SleepService, UtilsService } from "@services";
import { PayablesStore, PaymentsStore, TextChatStore, TasksStore, XactwareStore, AssignmentsStore, JobSharingStore } from "@stores";
import { Router } from '@angular/router';
import { Subscription } from "rxjs";
import { VersionReleaseDialogComponent } from "@shared-components/version-release-dialog";

@Component({
    selector: "side-menu",
    templateUrl: "side-menu.component.html",
    styleUrls: ['side-menu.component.css'],
    standalone: false
})

export class SideMenuComponent implements OnInit, OnDestroy, AfterViewInit {
    //@ViewChild('versionDialog') versionDialog: VersionReleaseDialogComponent; // Reference to the dialog component
    //@ViewChild('showReleaseNotesButton') showReleaseNotesButton!: ElementRef;

    showDialog = false;  // To control if the dialog is open or not

    firstName: string;
    profilePictureUrl: string;
    canAccessJobs: boolean;
    canAccessTasks: boolean;
    canAccessSchedule: boolean;
    canAccessCustomers: boolean;
    canAccessVendors: boolean;
    canAccessSubcontractor: boolean;
    // Australian companies call subcontractors "subbies".
    subcontractorsMenuLabel: string = GlobalsService.companyInfo?.isAustralia ? "Subbies" : "Subs";
    canAccessCommissions: boolean;
    canAccessBusinessDevelopment: boolean;
    canAccessEquipment: boolean;
    canAccessTimeclock: boolean;
    canAccessInventory: boolean;
    canAccessPropertyProfiles: boolean;
    canAccessUsers: boolean;
    canAccessSetup: boolean;
    canAccessQuickbooks: boolean;
    canAccessInvoices: boolean = false;
    canAccessPayments: boolean = false;
    canAccessPayables: boolean = false;
    canAccessTextChat: boolean = false;
    canAccessTeams: boolean = false;
    canAccessReporting: boolean = false;
    canAccessCustomReports: boolean = false;
    canAccessWorkOrders: boolean = false;
    canAccessEmailLogs: boolean = false;
    canAccessXactware: boolean = false;
    canAccessGlobalAdmin: boolean = (GlobalsService.isAdmin && GlobalsService.userInfo.isGlobalAdmin)
    // Learning Paths ships hidden until the API's LEARNING_PATHS_ENABLED turns it on. All three of its
    // links below (champion, branch, corporate) are Learning Paths, not Onboarding Setup.
    canAccessLearningPaths: boolean = GlobalsService.userInfo.isLearningPathsEnabled === true;
    // The champion editor (/learning-paths) is for independent accounts only — branches
    // can't edit their path, so they must not see this link (it would just error).
    canManageOnboarding: boolean = this.canAccessLearningPaths
        && (GlobalsService.isAdmin
            || GlobalsService.userInfo.isAdmin
            || GlobalsService.userInfo.isOnboardingChampion)
        && !(GlobalsService.companyInfo.parentCompanyId > 0);
    // Branch Admin/Champion: read-only onboarding progress (branches receive their path from Corporate).
    canViewBranchOnboarding: boolean = this.canAccessLearningPaths
        && (GlobalsService.companyInfo.parentCompanyId > 0)
        && (GlobalsService.userInfo.isAdmin || GlobalsService.userInfo.isOnboardingChampion);
    // Account setup checklist, shown until the account finishes it and then never again.
    // Job Sharing: only shown where somebody is actually linked to send us jobs. Both halves matter - the
    // permission alone would show an empty page to a company nobody can send to.
    canAccessSharedJobs: boolean = false;

    // Corporate shell. Every item in the corporate/dispatch menus is gated: the Corporate feature rows for the
    // corporate-only pages, and the standard View rows for the franchise views (they are the franchise's own
    // pages in an iframe, which enforce the same rows).
    canAccessCorporateDashboard: boolean = false;
    canAccessFranchiseJobs: boolean = false;
    canAccessFranchiseInvoices: boolean = false;
    canAccessFranchisePayments: boolean = false;
    canAccessFranchiseSchedule: boolean = false;
    canAccessFranchiseBusinessDev: boolean = false;
    canAccessFranchiseCommissions: boolean = false;
    canAccessCorporateKpis: boolean = false;
    canAccessCorporateReports: boolean = false;
    canAccessCorporatePriceBooks: boolean = false;
    canAccessCorporateUsers: boolean = false;
    canAccessDispatchUsers: boolean = false;
    canAccessCorporateOpenApi: boolean = false;
    canAccessCorporateTasks: boolean = false;

    get canAccessFranchiseViews(): boolean {
        return this.canAccessFranchiseJobs || this.canAccessFranchiseInvoices || this.canAccessFranchisePayments || this.canAccessFranchiseSchedule
            || this.canAccessFranchiseBusinessDev || this.canAccessFranchiseCommissions || this.canAccessCorporateTasks;
    }

    get canAccessCorporateAdminPages(): boolean {
        return this.canAccessCorporateUsers || this.canAccessDispatchUsers || this.canAccessCorporateOpenApi;
    }

    // Job Master + Billing in the Corporate/Dispatch portals are hidden per corporate account
    // until the Corporate permissions release gives them a real permission of their own.
    // Stacked on top of the Corporate/Reports permission (canAccessCorporateReports).
    canAccessCorporateJobMasterBillingReports: boolean = GlobalsService.canAccessCorporateReports;

    isAdmin: boolean = GlobalsService.isAdmin;
    isCorporateAccount: boolean = GlobalsService.companySettings.isCorporateAccount;
    isCorporateAdminUser: boolean = GlobalsService.userInfo.isCorporateAdmin;
    isDispatchUser: boolean = GlobalsService.userInfo.isDispatch;
    isLoggedIn: boolean;
    logoUrl = `https://docs.jobsight.com/company${GlobalsService.companyInfo.companyId}/logo.png?${new Date().getTime()}`;
    fallbackLogoUrl = '/content/images/icon.png';

    payablesAwaitingApprovalCount: number;
    payablesAwaitingApprovalCount$: Subscription;

    failedPaymentsCount: number;
    failedPaymentsCount$: Subscription;

    textChatUnreadMessagesCount: number;
    textChatUnreadMessageCount$: Subscription;
    textChatErrorMessagesCount: number;
    textChatErrorMessageCount$: Subscription;

    xactAssignmentCount: number;
    xactAssignmentCount$: Subscription;

    assignmentsBadge: number;

    sharedJobCount: number;
    sharedJobCount$: Subscription;

    useQuickbooksDesktop: boolean = GlobalsService.companySettings.useQuickbooksDesktop;
    useXactware: boolean = GlobalsService.companySettings.useXactware;

    isMenuExpanded: boolean = (localStorage.getItem("menuState") || 'expanded') === 'expanded';
    // Starts open when the user is already on one of its pages, so a refresh doesn't collapse the
    // group the current page lives under.
    isCorporateOpenApiMenuExpanded: boolean = window.location.pathname.startsWith("/corporate-open-api");
    jobSightId: string = GlobalsService.companySettings.jobSightId || '';
    hoveringSideMenu: boolean = false;
    useZift: boolean = GlobalsService.companySettings.useZift;
    minHeight: string;

    constructor(private authService: AuthService,
        private router: Router,
        private loginService: LoginService,
        private payablesStore: PayablesStore,
        private paymentsStore: PaymentsStore,
        private textChatStore: TextChatStore,
        private tasksStore: TasksStore,
        private assignmentsStore: AssignmentsStore,
        private jobSharingStore: JobSharingStore,
        private xactwareStore: XactwareStore) {
        this.isLoggedIn = GlobalsService.isLoggedIn;
        //if(GlobalsService.isDebug)
        //	this.logoUrl = `http://devdocs.jobsight.com/company${GlobalsService.companyInfo.companyId}/logo.png?${new Date().getTime()}`;

        if (this.isLoggedIn) {
            const employeeType = GlobalsService.userInfo.employeeType;
            this.canAccessJobs = GlobalsService.checkPermission("Jobs", "View");
            this.canAccessTasks = GlobalsService.checkPermission("Tasks", "View");
            this.canAccessSchedule = GlobalsService.checkPermission("Appointments", "View");
            this.canAccessCustomers = GlobalsService.checkPermission("Customers", "View");
            this.canAccessVendors = GlobalsService.checkPermission("Vendors", "View");
            this.canAccessSubcontractor = GlobalsService.checkPermission("Subcontractors", "View");
            this.canAccessBusinessDevelopment = GlobalsService.checkPermission("BusinessDevelopmentAccounts", "View");
            this.canAccessCommissions = GlobalsService.checkPermission("Commissions", "View");
            this.canAccessEquipment = GlobalsService.checkPermission("Equipment", "View");
            this.canAccessInvoices = GlobalsService.checkPermission("Invoices", "View");
            this.canAccessPayments = GlobalsService.checkPermission("Payments", "View");
            this.canAccessTimeclock = GlobalsService.checkPermission("Timeclock", "Access");
            this.canAccessPayables = GlobalsService.checkPermission("Payables", "View");
            this.canAccessInventory = GlobalsService.checkPermission("InventoryItems", "View");
            this.canAccessPropertyProfiles = GlobalsService.checkPermission("PropertyProfiles", "View");
            this.canAccessUsers = GlobalsService.checkPermission("Users", "Update|View") || GlobalsService.checkPermission("Teams", "View");
            this.canAccessSetup = GlobalsService.hasAnyPermission("Setup");
            this.canAccessQuickbooks = GlobalsService.checkPermission("Quickbooks", "Access");
            this.canAccessTextChat = GlobalsService.companySettings.useTextChat;
            this.canAccessTeams = GlobalsService.checkPermission("Teams", "View");
            this.canAccessReporting = GlobalsService.hasAnyPermission("Reporting");
            this.canAccessCustomReports = GlobalsService.checkPermission("CustomReporting", "View");
            this.canAccessWorkOrders = GlobalsService.checkPermission("WorkOrders", "View");
            this.canAccessEmailLogs = GlobalsService.checkPermission("EmailLogs", "Access");
            this.canAccessXactware = GlobalsService.checkPermission("Xactware", "Access");
            this.canAccessSharedJobs = (GlobalsService.companySettings.hasInboundJobSharingLinks === true) && GlobalsService.checkPermission("Jobs", "CanAcceptSharedJobs");

            // Corporate
            this.canAccessCorporateDashboard = GlobalsService.checkPermission("Corporate", "Dashboard");
            this.canAccessFranchiseJobs = GlobalsService.checkPermission("Jobs", "View");
            this.canAccessFranchiseInvoices = GlobalsService.checkPermission("Invoices", "View");
            this.canAccessFranchisePayments = GlobalsService.checkPermission("Payments", "View");
            this.canAccessFranchiseSchedule = GlobalsService.checkPermission("Appointments", "View");
            this.canAccessFranchiseBusinessDev = GlobalsService.checkPermission("BusinessDevelopmentAccounts", "View");
            // "Own commissions only" means nothing to a corporate user, so the corporate page gates on ShowAll.
            this.canAccessFranchiseCommissions = GlobalsService.checkPermission("Commissions", "ShowAll");
            this.canAccessCorporateKpis = GlobalsService.checkPermission("Corporate", "KPIs");
            this.canAccessCorporateReports = GlobalsService.checkPermission("Corporate", "Reports");
            this.canAccessCorporatePriceBooks = GlobalsService.checkPermission("Corporate", "PriceBook");
            this.canAccessCorporateUsers = GlobalsService.checkPermission("Corporate", "Users");
            this.canAccessDispatchUsers = GlobalsService.checkPermission("Corporate", "DispatchUsers");
            this.canAccessCorporateOpenApi = GlobalsService.checkPermission("Corporate", "OpenApi");
            this.canAccessCorporateTasks = GlobalsService.checkPermission("Tasks", "View");

            this.firstName = GlobalsService.userInfo.firstName;
            this.profilePictureUrl = GlobalsService.userInfo.profilePictureUrl;

            effect(() => {
                this.assignmentsBadge = this.assignmentsStore.assignmentsBadge();
            })
        }
    }

    ngOnInit() {
        if (GlobalsService.checkPermission("Payables", "View") && GlobalsService.checkPermission("Payables", "CanSeeAwaitingApproval")) {
            this.payablesAwaitingApprovalCount$ = this.payablesStore.payablesAwaitingApprovalCountSubject.subscribe(payablesAwaitingApprovalCount => this.payablesAwaitingApprovalCount = payablesAwaitingApprovalCount);
        }

        // Failed payments are only ever surfaced by the Zift webhook, so there's nothing to show
        // for a company that doesn't take card/ACH payments through us.
        if (this.useZift && this.canAccessPayments) {
            this.failedPaymentsCount$ = this.paymentsStore.failedPaymentsCountSubject.subscribe(failedPaymentsCount => this.failedPaymentsCount = failedPaymentsCount);
        }

        // The task count badge was removed from the main menu - the top menu tasks notification owns it now.

        if (this.canAccessSharedJobs) {
            this.sharedJobCount$ = this.jobSharingStore.sharedJobCountSubject.subscribe(sharedJobCount => this.sharedJobCount = sharedJobCount);
        }

        if (GlobalsService.companySettings.useTextChat) {
            this.textChatUnreadMessageCount$ = this.textChatStore.unreadMessagesCount$.subscribe(textChatUnreadMessagesCount => {
                this.textChatUnreadMessagesCount = textChatUnreadMessagesCount;
            });
            this.textChatErrorMessageCount$ = this.textChatStore.errorMessagesCount$.subscribe(textChatErrorMessagesCount => {
                this.textChatErrorMessagesCount = textChatErrorMessagesCount;
            });
        }

        if (GlobalsService.companySettings.useXactware) {
            this.xactAssignmentCount$ = this.xactwareStore.xactAssignmentCountSubject.subscribe(xactAssignmentCount => {
                this.xactAssignmentCount = xactAssignmentCount;
            });

        }

        this.minHeight = this.getMinHeight();

        // If this is a corporate user then they can click on a job and have it redirect to the job view page
        if (GlobalsService.userInfo.isDispatch || GlobalsService.userInfo.isCorporateAdmin) {
            this.tasksStore.loadJob$.subscribe(job => {
                this.router.navigate([`/franchise-page-view/jobs`],
                    { queryParams: { uuid: job.uuid } });
            });
        }

        if (this.isCorporateAccount && this.isDispatchUser) {
            this.assignmentsBadge = this.assignmentsStore.assignmentsBadge();

            this.assignmentsStore.startAssignmentPing();
        }
    }



    ngOnDestroy() {
        if (this.payablesAwaitingApprovalCount$)
            this.payablesAwaitingApprovalCount$.unsubscribe();

        if (this.sharedJobCount$)
            this.sharedJobCount$.unsubscribe();

        if (this.failedPaymentsCount$)
            this.failedPaymentsCount$.unsubscribe();
    }

    ngAfterViewInit() {
        if (this.isMenuExpanded)
            document.getElementById("main-area-container").classList.add('menu-is-open');

        const sidenav = document.querySelector('.sidenav') as HTMLElement;

        if (sidenav) {
            sidenav.addEventListener('mouseenter', () => {
                if (!sidenav.classList.contains('is-open')) {
                    this.hoveringSideMenu = true;
                }
            });

            sidenav.addEventListener('mouseleave', () => {
                if (!sidenav.classList.contains('is-open')) {
                    this.hoveringSideMenu = false;
                }
            });
        }
    }

    useFallbackLogo(event: Event) {
        (event.target as HTMLImageElement).src = this.fallbackLogoUrl;
    }

    toggleExpanded() {
        this.isMenuExpanded = !this.isMenuExpanded;
        document.getElementById("main-area-container").classList.toggle('menu-is-open');
        localStorage.setItem("menuState", this.isMenuExpanded ? 'expanded' : 'collapsed');
    }

    toggleCorporateOpenApiMenu() {
        this.isCorporateOpenApiMenuExpanded = !this.isCorporateOpenApiMenuExpanded;
    }

    async logout() {
        await this.loginService.logout("desktop");
        this.authService.clearToken();
        window.location.href = '/';
    }

    //async showVersionHistory() {
    //	this.versionDialog.showLastFiveVersions();
    //	await SleepService.sleep(250);
    //	this.showDialog = true;
    //}


    //onVersionDialogClose() {
    //	if (this.versionDialog?.dialogElement) {
    //		const dialogElement = this.versionDialog.dialogElement.nativeElement;
    //		const buttonRect = this.showReleaseNotesButton.nativeElement.getBoundingClientRect();
    //		const dialogRect = dialogElement.getBoundingClientRect();
    //		let offset = this.isMenuExpanded ? 120 : 10;
    //		const translateX = buttonRect.left - dialogRect.left + offset; // Adjust as needed
    //		const translateY = buttonRect.top - dialogRect.top + (buttonRect.height / 2) - (dialogRect.height / 2);

    //		dialogElement.style.transition = 'transform 0.7s cubic-bezier(0.8, 0, 0.2, 1), opacity 0.7s ease';
    //		dialogElement.style.transformOrigin = 'left center';

    //		// Start the fade-out animation along with the translation
    //		dialogElement.style.animation = 'fadeOut 0.7s forwards'; // Use the fadeOut keyframes
    //		dialogElement.style.transform = `translate(${translateX}px, ${translateY}px) scale(0.00)`; // Shrink and move the dialog

    //		setTimeout(() => {
    //			this.showDialog = false;
    //			dialogElement.style.transition = 'none';
    //			dialogElement.style.transform = 'translate(0, 0) scale(1)';
    //			dialogElement.style.opacity = '1'; // Reset opacity after closing
    //			dialogElement.style.animation = ''; // Remove the animation

    //			// Trigger the "pop" effect on the version release button after the dialog closes
    //			const buttonElement = this.showReleaseNotesButton.nativeElement;
    //			buttonElement.style.transition = 'transform 0.3s ease';
    //			buttonElement.style.transform = 'scale(1.1)'; // Slightly enlarge the button
    //			setTimeout(() => {
    //				buttonElement.style.transform = 'scale(1)'; // Return to normal size
    //			}, 300);
    //		}, 700); // Match the timing of the animation
    //	}
    //}
    //getReleaseNotesButton(): ElementRef {
    //	return this.showReleaseNotesButton;
    //}


    getMinHeight(): string {
        let px = 225;

        return `calc(100vh - ${px}px)`;
    }

    //idStartsWith911(): boolean {
    //	return this.jobSightId?.toString().startsWith('911');
    //}



}